import { __ } from '@wordpress/i18n';
import { useState } from '@wordpress/element';
import { calculateRoom, calculateTotals, CALC_CONSTANTS } from '../calc';
import downloadIcon from '../assets/img/download-icon.svg';

const HomeIcon = () => (
    <svg className="room-home-icon" width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path d="M1.5 17.475C1.08333 17.475 0.729167 17.3292 0.4375 17.0375C0.145833 16.7458 0 16.3917 0 15.975V14.075L4 10.525V17.475H1.5ZM5 17.475V13.475H13V17.475H5ZM14 17.475V9.275L9.725 5.475L12.75 2.8L17.5 7.025C17.6667 7.175 17.7917 7.34583 17.875 7.5375C17.9583 7.72917 18 7.93333 18 8.15V15.975C18 16.3917 17.8542 16.7458 17.5625 17.0375C17.2708 17.3292 16.9167 17.475 16.5 17.475H14ZM0 12.725V8.15C0 7.93333 0.0416667 7.725 0.125 7.525C0.208333 7.325 0.333333 7.15833 0.5 7.025L8 0.375C8.13333 0.241667 8.2875 0.145833 8.4625 0.0875C8.6375 0.0291667 8.81667 0 9 0C9.18333 0 9.3625 0.0291667 9.5375 0.0875C9.7125 0.145833 9.86667 0.241667 10 0.375L12 2.15L0 12.725Z" fill="currentColor"/>
    </svg>
);

function normalizeAchTarget(value) {
    if (value === 'mid' || value === 'balanced') {
        return 'balanced';
    }

    if (value === 'max' || value === 'maximum') {
        return 'maximum';
    }

    if (value === 'min' || value === 'minimum') {
        return 'minimum';
    }

    return '';
}

function isRoomComplete(room, minHeight) {
    const length = parseFloat(room.length) || 0;
    const width = parseFloat(room.width) || 0;
    const height = parseFloat(room.height) || 0;

    return (
        length > 0
        && width > 0
        && height >= minHeight
        && Boolean(room.roomType)
        && Boolean(normalizeAchTarget(room.achTarget))
        && Boolean(room.lighting)
    );
}

/**
 * Calculator Component (Standard HTML Version)
 *
 * @param {Object} props
 * @param {Array} props.rooms Array of room objects
 * @param {string} props.units 'imperial' or 'metric'
 * @param {Function} props.onUpdateRooms Callback to update rooms
 * @param {Function} props.onUpdateUnits Callback to update units
 * @param {Boolean} props.isEditor Whether we are in the editor context
 * @param {Function} props.onRequestDetailedReport Callback to open report popup
 */
export default function CalculatorForm({ rooms, units = 'imperial', onUpdateRooms, onUpdateUnits, isEditor = false, onRequestDetailedReport }) {
    const [bookmarkHint, setBookmarkHint] = useState(false);

    const handleBookmark = () => {
        setBookmarkHint(true);
        setTimeout(() => setBookmarkHint(false), 5000);
    };

    const updateRoom = (index, key, value) => {
        const newRooms = [...rooms];
        newRooms[index][key] = value;

        onUpdateRooms(newRooms);
    };

    const addRoom = () => {
        const newId = rooms.length > 0 ? Math.max(...rooms.map((room) => room.id)) + 1 : 1;
        const newRoom = {
            id: newId,
            name: `Classified Room ${newId}`,
            length: '',
            width: '',
            height: '',
            roomType: '',
            achTarget: '',
            lighting: ''
        };

        onUpdateRooms([...rooms, newRoom]);
    };

    const removeRoom = (index) => {
        const newRooms = rooms.filter((_, i) => i !== index);
        onUpdateRooms(newRooms);
    };

    const clearAll = () => {
        if (window.confirm(__('Are you sure you want to clear all fields?', 'allied-cleanroom-calculator'))) {
            onUpdateRooms([
                {
                    id: 1,
                    name: 'Classified Room 1',
                    length: '',
                    width: '',
                    height: '',
                    roomType: '',
                    achTarget: '',
                    notes: '',
                    lighting: ''
                }
            ]);
        }
    };

    const totals = calculateTotals(rooms, units);
    const unitLabel = units === 'imperial' ? 'ft' : 'm';
    const volumeLabel = units === 'imperial' ? 'ft\u00B3' : 'm\u00B3';
    const minHeight = units === 'imperial' ? CALC_CONSTANTS.MIN_HEIGHT_FT : CALC_CONSTANTS.MIN_HEIGHT_M;
    const canRequestReport = rooms.length > 0 && rooms.every((room) => isRoomComplete(room, minHeight));
    const calculatedRooms = rooms
        .map((room) => ({ room, results: calculateRoom(room, units) }))
        .filter((entry) => Boolean(entry.results));
    const complianceLabelByStatus = {
        compliant: __('Compliant ✓', 'allied-cleanroom-calculator'),
        below_min: __('Non-Compliant ×', 'allied-cleanroom-calculator'),
        above_recommended: __('Non-Compliant ×', 'allied-cleanroom-calculator'),
        unknown: __('Unknown', 'allied-cleanroom-calculator')
    };

    return (
        <div className="cleanroom-calculator-wrapper">
            <div className="cleanroom-inputs">
                <div className="cleanroom-header">
                    <div className="header-flex">
                        <h2>{__('Enter room dimensions and specifications below', 'allied-cleanroom-calculator')}</h2>
                        <div className="global-controls">
                            <select
                                id="unit-selector"
                                className="unit-selector"
                                value={units}
                                onChange={(event) => onUpdateUnits(event.target.value)}
                            >
                                <option value="imperial">{__('Imperial (ft)', 'allied-cleanroom-calculator')}</option>
                                <option value="metric">{__('Metric (m)', 'allied-cleanroom-calculator')}</option>
                            </select>
                            <button type="button" onClick={clearAll} className="btn-link clear-control">
                                {__('Clear all', 'allied-cleanroom-calculator')}
                            </button>
                        </div>
                    </div>
                </div>

                {rooms.map((room, index) => {
                    const results = calculateRoom(room, units);

                    return (
                        <div key={room.id} className="room-card safe-html-card">
                            <div className="room-card-header">
                                <HomeIcon />
                                <input
                                    className="room-card-title-input"
                                    type="text"
                                    value={room.name || `Classified Room ${index + 1}`}
                                    onChange={(event) => updateRoom(index, 'name', event.target.value)}
                                    aria-label={__('Room name', 'allied-cleanroom-calculator')}
                                />
                                {rooms.length > 1 && (
                                    <button
                                        type="button"
                                        className="btn-link-destructive"
                                        onClick={() => removeRoom(index)}
                                    >
                                        {__('Delete Room', 'allied-cleanroom-calculator')} <span aria-hidden="true">X</span>
                                    </button>
                                )}
                            </div>

                            <div className="room-card-body">
                                <div className="form-row three-cols">
                                    <div className="form-group">
                                        <label>{__(`Length (${unitLabel})`, 'allied-cleanroom-calculator')}</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={room.length}
                                            onChange={(event) => updateRoom(index, 'length', event.target.value)}
                                            min="0"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>{__(`Width (${unitLabel})`, 'allied-cleanroom-calculator')}</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={room.width}
                                            onChange={(event) => updateRoom(index, 'width', event.target.value)}
                                            min="0"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>{__(`Height (${unitLabel})`, 'allied-cleanroom-calculator')}</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={room.height}
                                            onChange={(event) => updateRoom(index, 'height', event.target.value)}
                                            min="0"
                                            step="0.01"
                                        />
                                        {parseFloat(room.height) > 0 && parseFloat(room.height) < minHeight && (
                                            <p className="field-warning">
                                                {__('Min. height:', 'allied-cleanroom-calculator')} {minHeight} {unitLabel}
                                            </p>
                                        )}
                                    </div>
                                </div>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label htmlFor={`room-type-${room.id}`}>{__('Room Type / Standard', 'allied-cleanroom-calculator')}</label>
                                        <select
                                            id={`room-type-${room.id}`}
                                            className="form-control"
                                            value={room.roomType}
                                            onChange={(event) => updateRoom(index, 'roomType', event.target.value)}
                                        >
                                            <option value="">{__('Select', 'allied-cleanroom-calculator')}</option>
                                            <optgroup label="ISO Standards">
                                                <option value="ISO 5">ISO 5 (Class 100)</option>
                                                <option value="ISO 6">ISO 6 (Class 1,000)</option>
                                                <option value="ISO 7">ISO 7 (Class 10,000)</option>
                                                <option value="ISO 8">ISO 8 (Class 100,000)</option>
                                            </optgroup>
                                            <optgroup label="USP Standards">
                                                <option value="USP 797/800 (ISO 7)">USP 797/800 Buffer (ISO 7)</option>
                                                <option value="USP 797/800 (ISO 8)">USP 797/800 Ante (ISO 8)</option>
                                            </optgroup>
                                            <optgroup label="cGMP Standards">
                                                <option value="cGMP Grade A">cGMP Grade A</option>
                                                <option value="cGMP Grade B">cGMP Grade B</option>
                                                <option value="cGMP Grade C">cGMP Grade C</option>
                                                <option value="cGMP Grade D">cGMP Grade D</option>
                                            </optgroup>
                                        </select>
                                    </div>
                                </div>

                                <div className="form-row two-cols">
                                    <div className="form-group">
                                        <label htmlFor={`ach-target-${room.id}`}>{__('Target ACH Level', 'allied-cleanroom-calculator')}</label>
                                        <select
                                            id={`ach-target-${room.id}`}
                                            className="form-control"
                                            value={normalizeAchTarget(room.achTarget)}
                                            onChange={(event) => updateRoom(index, 'achTarget', event.target.value)}
                                        >
                                            <option value="">{__('Select', 'allied-cleanroom-calculator')}</option>
                                            <option value="minimum">{__('Minimum', 'allied-cleanroom-calculator')}</option>
                                            <option value="balanced">{__('Balanced', 'allied-cleanroom-calculator')}</option>
                                            <option value="maximum">{__('Maximum', 'allied-cleanroom-calculator')}</option>
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor={`lighting-${room.id}`}>{__('Lighting Requirements', 'allied-cleanroom-calculator')}</label>
                                        <select
                                            id={`lighting-${room.id}`}
                                            className="form-control"
                                            value={room.lighting}
                                            onChange={(event) => updateRoom(index, 'lighting', event.target.value)}
                                        >
                                            <option value="">{__('Select', 'allied-cleanroom-calculator')}</option>
                                            <option value="regular">{__('Regular', 'allied-cleanroom-calculator')}</option>
                                            <option value="high">{__('High Intensity', 'allied-cleanroom-calculator')}</option>
                                            <option value="very_high">{__('Very High', 'allied-cleanroom-calculator')}</option>
                                        </select>
                                    </div>
                                </div>

                                {results && (
                                    <div className="room-results-wrap">
                                        <h4>{__('Results', 'allied-cleanroom-calculator')}</h4>
                                        <div className="room-inline-result">
                                            <div className="result-pill">
                                                <span>{__('Volume', 'allied-cleanroom-calculator')}</span>
                                                <strong>{results.volume.toLocaleString()} {volumeLabel}</strong>
                                            </div>
                                            <div className="result-pill">
                                                <span>{__('ACH', 'allied-cleanroom-calculator')}</span>
                                                <strong>{results.ach}</strong>
                                            </div>
                                            <div className="result-pill">
                                                <span>{__('CFM', 'allied-cleanroom-calculator')}</span>
                                                <strong>{results.cfm.toLocaleString()}</strong>
                                            </div>
                                            <div className={`result-pill compliance compliance--${results.complianceStatus}`}>
                                                <span>{__('Compliance', 'allied-cleanroom-calculator')}</span>
                                                <strong>{complianceLabelByStatus[results.complianceStatus] || complianceLabelByStatus.unknown}</strong>
                                            </div>
                                        </div>

                                        <div className="notes-recommendation">
                                            <span>{__('Notes / Recommendations', 'allied-cleanroom-calculator')}</span>
                                            <p>{results.recommendedAction}</p>
                                            <small className="recommendation-auto-note">{__('This recommendation is generated automatically based on selected standard and ACH target.', 'allied-cleanroom-calculator')}</small>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}

                <button type="button" onClick={addRoom} className="call-style-button add-room-button" data-text={__('Add Room', 'allied-cleanroom-calculator')}>
                    <span className="call-style-button__label">{__('Add Room', 'allied-cleanroom-calculator')}</span>
                    <span className="call-style-button__icon-box">+</span>
                </button>
            </div>

            <div className="cleanroom-results-preview">
                <h3>{__('Calculation Summary', 'allied-cleanroom-calculator')}</h3>

                <div className="results-summary">
                    <div className="result-item big">
                        <span className="label">{__('Total Airflow Required', 'allied-cleanroom-calculator')}</span>
                        <span className="value">{totals.totalCfm.toLocaleString()} <small>CFM</small></span>
                    </div>

                    {calculatedRooms.length > 0 && (
                        <div className="summary-table-wrap">
                            <table className="summary-table">
                                <thead>
                                    <tr>
                                        <th aria-label={__('Metric', 'allied-cleanroom-calculator')}></th>
                                        {calculatedRooms.map(({ room }) => (
                                            <th key={`header-${room.id}`}>{room.name}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>{__('Volume', 'allied-cleanroom-calculator')}</td>
                                        {calculatedRooms.map(({ room, results }) => (
                                            <td key={`volume-${room.id}`}>{results.volume.toLocaleString()} {volumeLabel}</td>
                                        ))}
                                    </tr>
                                    <tr>
                                        <td>{__('ACH', 'allied-cleanroom-calculator')}</td>
                                        {calculatedRooms.map(({ room, results }) => (
                                            <td key={`ach-${room.id}`}>{results.ach}</td>
                                        ))}
                                    </tr>
                                    <tr>
                                        <td>{__('CFM', 'allied-cleanroom-calculator')}</td>
                                        {calculatedRooms.map(({ room, results }) => (
                                            <td key={`cfm-${room.id}`}>{results.cfm.toLocaleString()}</td>
                                        ))}
                                    </tr>
                                    <tr>
                                        <td>{__('Compliance', 'allied-cleanroom-calculator')}</td>
                                        {calculatedRooms.map(({ room, results }) => (
                                            <td key={`compliance-${room.id}`}>{(complianceLabelByStatus[results.complianceStatus] || complianceLabelByStatus.unknown).replace(' ✓', '')}</td>
                                        ))}
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    )}

                    <div className="result-grid compact">
                        <div className="result-item result-item--inline">
                            <span className="label">{__('HEPA Filters', 'allied-cleanroom-calculator')}</span>
                            <span className="value">{totals.totalHepa} <small>(~600 CFM/ea)</small></span>
                        </div>
                        <div className="result-item result-item--inline">
                            <span className="label">{__('Low Air Returns', 'allied-cleanroom-calculator')}</span>
                            <span className="value">{totals.totalReturns} <small>(~500 CFM/ea)</small></span>
                        </div>
                    </div>
                    <p className="result-step-note">{__('HEPA and returns update in steps when total CFM passes each threshold (600 and 500 CFM).', 'allied-cleanroom-calculator')}</p>
                </div>

                <div className="cta-preview">
                    <button
                        type="button"
                        className="call-style-button report-button"
                        data-text={__('Get Detailed Report', 'allied-cleanroom-calculator')}
                        onClick={() => onRequestDetailedReport && onRequestDetailedReport({ rooms, units, totals })}
                        disabled={!canRequestReport || isEditor}
                    >
                        <span className="call-style-button__label">{__('Get Detailed Report', 'allied-cleanroom-calculator')}</span>
                        <span className="call-style-button__icon-box download-icon-box">
                            <img src={downloadIcon} alt="" aria-hidden="true" />
                        </span>
                    </button>
                    {!isEditor && !canRequestReport && (
                        <p className="tiny-text tiny-text--warning">{__('Fill out all the room(s) fields to download your report', 'allied-cleanroom-calculator')}</p>
                    )}
                    {isEditor && (
                        <p className="tiny-text">{__('Button is enabled on frontend when all required fields are completed.', 'allied-cleanroom-calculator')}</p>
                    )}
                </div>

                {!isEditor && (
                    <div className="bookmark-wrap">
                        <button type="button" className="bookmark-btn" onClick={handleBookmark}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                <path d="M5 3H19C19.5523 3 20 3.44772 20 4V21.2768C20 21.5946 19.7558 21.7932 19.5 21.7071L12 19.1213L4.5 21.7071C4.24421 21.7932 4 21.5946 4 21.2768V4C4 3.44772 4.44772 3 5 3Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            {__('Bookmark this page', 'allied-cleanroom-calculator')}
                        </button>
                        {bookmarkHint && (
                            <p className="bookmark-hint" role="status">
                                {__('Press', 'allied-cleanroom-calculator')} <kbd>Ctrl+D</kbd> {__('on Windows or', 'allied-cleanroom-calculator')} <kbd>⌘D</kbd> {__('on Mac to save this page.', 'allied-cleanroom-calculator')}
                            </p>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
